import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useAnalytics, ANALYTICS_EVENTS } from '@core/analytics';
import { useBooking } from '@features/booking/state';

type FilterMode = 'any' | 'specific';

/**
 * Step 3C — Menu pre-selection (filter)
 *
 * Two modes:
 * - "Any experience": skip filtering, show all available time slots.
 * - "Search by experience": user selects specific menus to narrow
 *   which time slots are shown at the TIME step.
 *
 * This step only saves the filter selection in booking state.
 * The actual slot filtering happens in useSelectTimeState (frontend filter),
 * so we never re-fetch search-all-hours here — booking.timeSlots stays intact.
 */
const SelectMenuFilterPage = () => {
  const { t } = useTranslation('booking');
  const { goToNext } = useBookingFlow();
  const { trackEvent } = useAnalytics();
  const booking = useBooking();

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_SELECT_MENU_FILTER });
  }, [trackEvent]);

  // Gather distinct menus from the FULL (unfiltered) time slots
  const distinctMenus = useMemo(() => {
    const slots = booking.timeSlots ?? [];
    const menuMap = new Map<number, string>();
    for (const slot of slots) {
      for (const menu of slot.menus) {
        if (!menuMap.has(menu.id)) {
          menuMap.set(menu.id, menu.name);
        }
      }
    }
    return Array.from(menuMap.entries()).map(([id, name]) => ({ id, name }));
  }, [booking.timeSlots]);

  // Restore previous selection state
  const [filterMode, setFilterMode] = useState<FilterMode>(() => {
    if (booking.selectedMenuFilter && booking.selectedMenuFilter.length > 0) {
      return 'specific';
    }
    return 'any';
  });

  const [selected, setSelected] = useState<Set<number>>(() => {
    if (booking.selectedMenuFilter) {
      return new Set(booking.selectedMenuFilter);
    }
    return new Set<number>();
  });

  const toggleMenu = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleModeChange = (mode: FilterMode) => {
    setFilterMode(mode);
    if (mode === 'any') {
      setSelected(new Set());
    }
  };

  const handleContinue = () => {
    const filter = filterMode === 'specific' && selected.size > 0
      ? Array.from(selected)
      : null;
    booking.setSelectedMenuFilter(filter);
    goToNext();
  };

  const canContinue = filterMode === 'any' || selected.size > 0;

  return (
    <BookingPageLayout
      title=""
      subtitle=""
      breadcrumbs={<BookingBreadcrumbs />}
    >
      {/* Mode selection */}
      <div className="w-full px-6 space-y-3">
        <button
          type="button"
          onClick={() => handleModeChange('any')}
          className={`w-full py-4 px-5 rounded-xl border text-left transition-all duration-200 flex items-center justify-between ${
            filterMode === 'any'
              ? 'bg-brand text-brand-foreground border-brand shadow-sm'
              : 'bg-card text-foreground/70 border-border hover:border-brand-light'
          }`}
        >
          <span className="font-medium">{t('SEARCH_BY_ANY_EXPERIENCE')}</span>
          {filterMode === 'any' && <Check className="w-5 h-5 shrink-0" />}
        </button>

        <button
          type="button"
          onClick={() => handleModeChange('specific')}
          className={`w-full py-4 px-5 rounded-xl border text-left transition-all duration-200 flex items-center justify-between ${
            filterMode === 'specific'
              ? 'bg-brand text-brand-foreground border-brand shadow-sm'
              : 'bg-card text-foreground/70 border-border hover:border-brand-light'
          }`}
        >
          <span className="font-medium">{t('SEARCH_BY_EXPERIENCE')}</span>
          {filterMode === 'specific' && <Check className="w-5 h-5 shrink-0" />}
        </button>
      </div>

      {/* Menu list (shown when "specific" mode is active) */}
      {filterMode === 'specific' && (
        <div className="w-full px-6 mt-6 space-y-2">
          <p className="text-sm text-foreground/50 mb-3">
            {t('MENU_FILTER_SELECT_MENUS')}
          </p>
          {distinctMenus.map((menu) => (
            <button
              key={menu.id}
              type="button"
              onClick={() => toggleMenu(menu.id)}
              className={`w-full py-3 px-4 rounded-lg border text-left transition-all duration-200 flex items-center gap-3 ${
                selected.has(menu.id)
                  ? 'bg-brand/10 text-foreground border-brand'
                  : 'bg-card text-foreground/70 border-border hover:border-brand-light'
              }`}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                selected.has(menu.id)
                  ? 'bg-brand border-brand'
                  : 'border-foreground/30'
              }`}>
                {selected.has(menu.id) && <Check className="w-3.5 h-3.5 text-brand-foreground" />}
              </div>
              <span className="font-medium text-sm">{menu.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="w-full px-6 mt-12">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleContinue}
          disabled={!canContinue}
        >
          {t('FILTER_SELECT_MENU_EXPERIENCE_APPLY')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default SelectMenuFilterPage;
